import React, { useContext, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppDataProvider/AppDataProvider";
import { ActionType } from "../../AppDataProvider/actions";
import { Button } from "../../common/components";
import { marginBottom, marginTop, paddingX, typography } from "../../common/styles";
import { applyOpacity } from "../../common/styles/utils";
const REMOVE_FROM_WAITLIST_MUTATION = gql`
  mutation RemoveFromWaitlist($input: RemoveFromWaitlistInput!) {
    removeFromWaitlist(input: $input) {
      success
      message
    }
  }
`;
type RemoveFromWaitlistData = {
  removeFromWaitlist: {
    success: boolean;
    message: string | null;
  } | null;
};
type RemoveFromWaitlistVariables = {
  input: {
    organisationId: string;
    waitlistEntryId: string;
    token: string;
  };
};
export function RemoveWaitlistStep(): React.ReactElement {
  const {
    t
  } = useTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  const [removed, setRemoved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const [removeFromWaitlist, {
    loading
  }] = useMutation<RemoveFromWaitlistData, RemoveFromWaitlistVariables>(REMOVE_FROM_WAITLIST_MUTATION);

  // Parse URL params for waitlist entry details
  const urlParams = new URLSearchParams(window.location.search);
  const waitlistEntryId = urlParams.get("waitlistEntryId");
  const token = urlParams.get("token");
  const organisationId = urlParams.get("organisationId") ?? appData.bookingData.organisation?._id ?? (appData.bookingTool.organisations.edges.length === 1 ? appData.bookingTool.organisations.edges[0].node._id : undefined);
  const handleRemove = async () => {
    if (!waitlistEntryId || !token || !organisationId) {
      setError(t("waitlist.invalidLink", "This removal link is invalid or has expired."));
      return;
    }
    setError(null);
    try {
      const result = await removeFromWaitlist({
        variables: {
          input: {
            organisationId,
            waitlistEntryId,
            token
          }
        }
      });
      if (result.data?.removeFromWaitlist?.success) {
        setRemoved(true);
        // Clear the removal params on success too, so refreshing or
        // reopening this URL doesn't re-attempt removing an entry that's
        // already been removed.
        const url = new URL(window.location.href);
        url.searchParams.delete("action");
        url.searchParams.delete("waitlistEntryId");
        url.searchParams.delete("token");
        window.history.replaceState(null, "", url.toString());
      } else {
        setError(result.data?.removeFromWaitlist?.message ?? t("waitlist.removeError", "Something went wrong. Please try again."));
      }
    } catch {
      setError(t("waitlist.removeError", "Something went wrong. Please try again."));
    }
  };
  const handleGoBack = () => {
    // Clear the removal params so a refresh/restart doesn't keep landing
    // back on this step.
    const url = new URL(window.location.href);
    url.searchParams.delete("action");
    url.searchParams.delete("waitlistEntryId");
    url.searchParams.delete("token");
    window.history.replaceState(null, "", url.toString());
    const {
      organisation,
      createdSource,
      service,
      timezone
    } = appData.bookingData;
    if (organisation && createdSource && service && timezone) {
      // We have all the booking data the Availability step requires (e.g.
      // this step was somehow reached from within an in-progress booking
      // flow), so navigating back into the normal flow is safe.
      appDataDispatch({
        type: ActionType.BACK_FROM_WAITLIST_STEP
      });
      return;
    }

    // This step is normally the sole entrypoint into the app (arrived at
    // cold via a removal link, with no organisation/service/booking ever
    // loaded), so BACK_FROM_WAITLIST_STEP's target ("availability") isn't
    // safe here — that step requires booking data that doesn't exist and
    // can throw. Just show a neutral "cancelled" state locally instead of
    // dispatching a global navigation change.
    setCancelled(true);
  };
  if (cancelled) {
    return <Container>
        <Title>{t("waitlist.removeTitle", "Remove from waitlist")}</Title>
        <Message>
          {t("waitlist.removeCancelled", "You can close this window.")}
        </Message>
      </Container>;
  }
  if (removed) {
    return <Container>
        <StatusIcon>&#10003;</StatusIcon>
        <Title>
          {t("waitlist.removedTitle", "You've been removed from the waitlist")}
        </Title>
        <Message>
          {t("waitlist.removedMessage", "You will no longer receive notifications about this event.")}
        </Message>
      </Container>;
  }
  if (!waitlistEntryId || !token || !organisationId) {
    return <Container>
        <Title>{t("waitlist.removeTitle", "Remove from waitlist")}</Title>
        <ErrorMessage>
          {t("waitlist.invalidLink", "This removal link is invalid or has expired.")}
        </ErrorMessage>
      </Container>;
  }
  return <Container data-sentry-element="Container" data-sentry-component="RemoveWaitlistStep" data-sentry-source-file="RemoveWaitlistStep.tsx">
      <Title data-sentry-element="Title" data-sentry-source-file="RemoveWaitlistStep.tsx">{t("waitlist.removeTitle", "Remove from waitlist")}</Title>
      <Message data-sentry-element="Message" data-sentry-source-file="RemoveWaitlistStep.tsx">
        {t("waitlist.removeConfirmMessage", "Are you sure you want to remove yourself from the waitlist? You will no longer be notified when a spot becomes available.")}
      </Message>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ButtonGroup data-sentry-element="ButtonGroup" data-sentry-source-file="RemoveWaitlistStep.tsx">
        <RemoveButton onClick={() => {
        void handleRemove();
      }} disabled={loading} data-sentry-element="RemoveButton" data-sentry-source-file="RemoveWaitlistStep.tsx">
          {loading ? t("waitlist.removing", "Removing...") : t("waitlist.removeButton", "Remove me")}
        </RemoveButton>
        <CancelLink onClick={handleGoBack} data-sentry-element="CancelLink" data-sentry-source-file="RemoveWaitlistStep.tsx">{t("cancel", "Cancel")}</CancelLink>
      </ButtonGroup>
    </Container>;
}
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;
const StatusIcon = styled.div`
  font-size: 2.5rem;
  ${marginBottom.m}
  color: ${({
  theme
}) => theme.colours.primary};
`;
const Title = styled.h2`
  ${typography.heading}
  ${marginBottom.m}
`;
const Message = styled.p`
  ${typography.body}
  ${marginBottom.l}
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.7)};
`;
const ErrorMessage = styled.div`
  ${typography.small}
  ${marginTop.m}
  ${marginBottom.m}
  color: ${({
  theme
}) => theme.colours.error ?? "#d32f2f"};
`;
const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 1rem;
`;
const RemoveButton = styled(Button)`
  width: 100%;
  ${paddingX.l}
  background-color: ${({
  theme
}) => theme.colours.error ?? "#d32f2f"};
  color: white;

  &:hover {
    opacity: 0.9;
  }
`;
const CancelLink = styled.button`
  ${typography.body}
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)};
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${({
  theme
}) => theme.colours.dark};
  }
`;